<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import type { File } from '@/types/core';
//#endregion

//#region types
interface Props {
  files?: File[],
  canUpload?: boolean,
}

interface Emits {
  'attach-file': []
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  files: () => [],
  canUpload: false,
});
const emit = defineEmits<Emits>();
//#endregion

//#region computed
const previewImagesSrcList = computed(() => {
  return (
    props.files
      .filter((img) => img.preview_url !== null)
      .map((img) => img.preview_url!) || []
  );
});
//#endregion

//#region methods
function handlerAttachFile() {
  emit('attach-file');
}
//#endregion
</script>

<template>
  <CardBlock
    class="files-list"
    :title="$t('mediaBuying.info.files_card.title')"
    :no-data-text="$t('mediaBuying.info.files_card.no_files')"
  >
    <template
      v-if="props.canUpload"
      v-slot:action
    >
      <UiAction
        :action-name="$t(`affiliates.affiliatesInfo.edit`)"
        @click="handlerAttachFile"
      />
    </template>
    <template
      v-if="props.files"
      v-slot:cardItem
    >
      <template
        v-for="(file, index) in props.files"
        :key="index"
      >
        <CardItem
          class="files-list__file"
          :no-border="index === 0"
        >
          <template v-slot:title>
            <div class="files-list__file-title t:truncate">
              <div class="t:truncate">
                {{ file.filename }}
                <ElImage
                  v-if="file.mime_type === 'image/jpeg' || file.mime_type === 'image/png'"
                  :src="file.preview_url!"
                  :preview-src-list="previewImagesSrcList"
                  :fit="'contain'"
                />
              </div>
              <UiDropdown
                v-if="file.mime_type === 'image/jpeg' || file.mime_type === 'image/png'"
                trigger="hover"
                popper-class="files-list__img-preview-popper"
              >
                <UiIcon
                  color="var(--text-primary)"
                  name="eye"
                />
                <template v-slot:dropdown>
                  <img
                    :src="file.preview_url!"
                    alt=""
                  >
                </template>
              </UiDropdown>
            </div>
          </template>
          <template v-slot:value>
            <a
              class="files-list__file-value"
              :href="file.download_url"
              :download="file.filename"
              target="_blank"
            >
              <UiIcon
                name="download"
                color="inherit"
              />
              {{ $t(`mediaBuying.info.files_card.download`) }}
            </a>
          </template>
        </CardItem>
      </template>
    </template>
  </CardBlock>
</template>

<style lang="scss">
.files-list {
  $root: &;

  &__file-title{
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;

    .el-image{
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      img{
        width: 100%;
        opacity: 0;
      }
      &-viewer__canvas{
        img{
          width: auto;
          opacity: 1;
        }
      }
    }
  }
  &__file-value{
    cursor: pointer;
    font-size: 12px;
    align-items: center;
    color: var(--text-primary);
    gap: 4px;
    display: none;
  }
  &__file {
    &:hover {
      #{$root}__file-value {
        display: flex !important;
      }
    }
  }
  &__img-preview-popper{
    &.el-dropdown__popper.el-popper {
      padding: 16px;
      max-width: 332px;
      border-radius: 8px;
      box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
      background-color: #ffffff;
      .el-dropdown-menu{
        pointer-events: none;
        padding: 0;
        background-color: transparent;
        line-height: 0;
        img{
          max-width: 300px;
          max-height: 240px;
        }
      }
    }
  }
}
</style>
